/**
 * Agent Login Page
 * 
 * Dedicated login page for agents with:
 * - Agent-specific branding
 * - Direct login to agent dashboard
 * - Link to accept invitations
 */

import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useQueryClient } from '@tanstack/react-query'
import { authKeys } from '../../hooks/useAuth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Alert } from '../../components/ui/Alert'
import { 
  LogIn, 
  Mail, 
  Lock, 
  RefreshCw, 
  XCircle,
  Users,
  ArrowRight
} from 'lucide-react'

import { api } from '../../services/api'
import { logger } from '../../utils/logger'

interface AgentLoginData {
  email: string
  password: string
}

export const AgentLogin: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  
  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/agent/dashboard')
    }
  }, [isAuthenticated, navigate])
  
  const [formData, setFormData] = useState<AgentLoginData>({
    email: '',
    password: ''
  })
  
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      setIsLoading(true)
      setError(null)
      
      const response = await api.post('/agents/auth/login', formData)
      
      console.log('🔍 Agent login response:', response.data)
      
      // Create user data for React Query cache
      const userData = {
        id: response.data.user_id || 'temp_id',
        email: formData.email,
        full_name: response.data.full_name || formData.email,
        is_active: true,
        user_type: response.data.user_type || 'agent'
      }

      // Store authentication token in the same format as company owner login
      const authData = {
        state: {
          user: userData,
          token: response.data.access_token,
          isAuthenticated: true
        }
      }
      
      // Set user_type FIRST before anything else
      localStorage.setItem('user_type', response.data.user_type || 'agent')
      localStorage.setItem('bgai-auth-storage', JSON.stringify(authData))
      
      // Set token in axios headers immediately
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`
      
      // Update React Query cache with user data
      queryClient.setQueryData(authKeys.user(), userData)
      
      logger.info('Agent login successful', {
        component: 'AgentLogin',
        email: formData.email,
        redirectTo: '/agent/dashboard',
        userType: response.data.user_type
      })
      
      // Use React Router navigation
      navigate('/agent/dashboard')
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      setError(errorMessage)
      
      logger.error('Agent login failed', {
        component: 'AgentLogin',
        email: formData.email,
        error: errorMessage
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Agent Login Card */}
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Agent Portal</CardTitle>
            <CardDescription>
              Sign in to your agent dashboard to manage conversations and support customers
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6 pt-0">
            {error && (
              <Alert className="bg-red-50 border-red-200 mb-6">
                <XCircle className="w-5 h-5 text-red-600" />
                <div>
                  <h4 className="font-medium text-red-600 mb-1">Login Failed</h4>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </Alert>
            )}
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="agent@company.com"
                  required
                  autoComplete="email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Lock className="w-4 h-4 inline mr-1" />
                  Password
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
              </div>
              
              <Button
                type="submit"
                disabled={isLoading || !formData.email || !formData.password}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In to Agent Portal
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Invitation Link */}
        <Card className="mt-4 shadow-lg">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">
                New agent? Have an invitation link?
              </p>
              <Link to="/agent/accept-invitation">
                <Button variant="outline" className="w-full">
                  Accept Team Invitation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        
        {/* Company Login Link */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Company owner?{' '}
            <Link to="/auth/login" className="text-blue-600 hover:text-blue-700 font-medium">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default AgentLogin